import React, { useState } from "react";
import _ from "lodash";
import { ExcelRenderer, OutTable } from "react-excel-renderer";
import {
  Box,
  Button,
  Dialog,
  makeStyles,
  Snackbar,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import CardHeader from "../../components/Card/CardHeader";
import CardBody from "../../components/Card/CardBody";
import Card from "../../components/Card/Card";
import axios from "axios";
import { baseUrl } from "../../config/api";
import MuiAlert from "@material-ui/lab/Alert";
import { useSelector } from "react-redux";

const styles = {
  formControl: {
    paddingBottom: "10px",
    margin: "27px 0 0 0",
    position: "relative",
    verticalAlign: "unset",
  },
  cardCategoryWhite: {
    color: "rgba(255,255,255,.62)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
  },
};

const useStyles = makeStyles(styles);

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export function ImportExcel(props) {
  const { open, close, db } = props;
  let authToken = useSelector((state) => state.userReducer.authToken);
  let userInfo = useSelector((state) => state.userReducer.userInfo);
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [alertObject, setAlertObject] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertObject(null);
  };

  function validateData(data) {
    const errors = [];
    const requiredFields = getRequiredFields(db);

    data.forEach((row, index) => {
      // Check basic required fields
      requiredFields.forEach(field => {
        const value = row[field];
        // Treat 0 / false as valid; only flag truly missing/empty
        if (value === undefined || value === null || value === '') {
          errors.push(`Row ${index + 2}: Missing required field '${field}'`);
        }
      });

      // Additional validation for readyMasters
      if (db === 'readyMasters') {
        // Must have either productCode OR (color + sph + cyl + axis + addition)
        const hasProductCode = row.productCode && row.productCode !== '';
        const hasComponents = row.color && row.sph && row.cyl && row.axis && row.addition;
        
        if (!hasProductCode && !hasComponents) {
          errors.push(
            `Row ${index + 2}: Must have either 'productCode' OR all of (color, sph, cyl, axis, addition)`
          );
        }
      }

      // Additional validation for offersMasters:
      // At least one of offerAmount or offerPercent should be present
      if (db === 'offersMasters') {
        const hasOfferAmount = row.offerAmount !== undefined && row.offerAmount !== '';
        const hasOfferPercent = row.offerPercent !== undefined && row.offerPercent !== '';
        if (!hasOfferAmount && !hasOfferPercent) {
          errors.push(
            `Row ${index + 2}: Must have at least one of 'offerAmount' or 'offerPercent'`
          );
        }
      }
    });

    return errors;
  }

  function getRequiredFields(dbType) {
    switch (dbType) {
      case 'productMasters':
        // Practically required: productName, lab, index, price
        return ['productName', 'lab', 'index', 'price'];
      case 'fittingMasters':
        return ['fittingType', 'amount'];
      case 'readyMasters':
        // ProductCode can be auto-generated from color+sph+cyl+axis+addition
        // OR provided directly. Require productName and rate for meaningful stock.
        return ['productName', 'rate'];
      case 'challans':
        // Practically required for meaningful transaction from Excel
        // Columns used in template: party, amount, packagingCost, createdAt, items
        return ['party', 'amount', 'createdAt', 'items'];
      case 'credits':
        // Similar shape to challans
        return ['party', 'amount', 'createdAt', 'items'];
      case 'debits':
        // Same as credits
        return ['party', 'amount', 'createdAt', 'items'];
      case 'accounts':
        // Schema requires name; city/state strongly recommended but optional here
        return ['name'];
      case 'discounts':
        // No strict backend shape; driven by frontend expectations
        return ['accountCode', 'productName'];
      case 'readyMasterRange':
        // Practically required to behave like normal create logic
        // (keep 'type' optional to allow your current sheets)
        return [
          'productName',
          'index',
          'side',
          'color',
          'rate',
          'startSph',
          'endSph',
          'startCyl',
          'endCyl',
          'startAddition',
          'endAddition',
          'axis',
        ];
      case 'payments':
        // Practically required: party, amount, paymentFor, paymentDate
        return ['party', 'amount', 'paymentFor', 'paymentDate'];
      case 'brandMasters':
        return ['name'];
      case 'categoryMasters':
        return ['name'];
      case 'offersMasters':
        // offerName plus at least one of offerAmount/offerPercent (checked above)
        return ['offerName'];
      case 'trendingMasters':
        return ['readyMasterRangeId'];
      case 'banks':
        // Practically required: bankName, acNo, ifscCode, branch
        return ['bankName', 'acNo', 'ifscCode', 'branch'];
      case 'lab':
        return ['labName'];
      default:
        return [];
    }
  }

  function _changeHandler(event) {
    let fileObj = event.target.files[0];
    setValidationErrors([]);

    if (fileObj) {
      ExcelRenderer(fileObj, (err, resp) => {
        if (err) {
          console.log(err);
          setAlertObject({
            status: "error",
            message: "Error reading file. Please check file format."
          });
        } else {
          setCols(resp.cols);
          setRows(resp.rows);
          let rowHeaders = resp.rows[0];
          let rows = resp.rows;
          let data = [];

          for (let i = 1; i <= rows.length; i++) {
            if (rows[i]) {
              let object = {};
              rows[i].map((item, index) => {
                _.merge(object, { [rowHeaders[index]]: item });
              });
              data.push(object);
            }
          }

          // Validate data
          const errors = validateData(data);
          setValidationErrors(errors);

          if (errors.length === 0) {
            setTableData(data);
            setAlertObject({
              status: "success",
              message: `File loaded successfully. ${data.length} records ready to upload.`
            });
          } else {
            setAlertObject({
              status: "warning",
              message: `File has validation errors. Please fix them before uploading.`
            });
          }
        }
      });
    }
  }

  async function _onUpload() {
    if (validationErrors.length > 0) {
      setAlertObject({
        status: "error",
        message: "Please fix validation errors before uploading",
      });
      return;
    }

    if (tableData.length === 0) {
      setAlertObject({
        status: "error",
        message: "No data to upload. Please select a file first.",
      });
      return;
    }

    setIsUploading(true);
    try {
      // Add entity to each row for backend compatibility
      const dataWithEntity = tableData.map(row => ({
        ...row,
        entity: userInfo.entity
      }));

      let url = `${baseUrl}/${db}/bulk-upload`;
      let res = await axios.post(
        url,
        { data: dataWithEntity, entity: userInfo.entity },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      
      // Handle both old string response and new JSON response
      const successMessage = res.data?.message || `Successfully uploaded ${tableData.length} records`;
      
      // Show detailed results if available
      let resultMessage = successMessage;
      if (res.data?.created !== undefined) {
        resultMessage = `✅ Created: ${res.data.created}, Updated: ${res.data.updated}`;
        if (res.data.errors && res.data.errors.length > 0) {
          resultMessage += ` | ⚠️ Errors: ${res.data.errors.length}`;
          console.error('Import errors:', res.data.errors);
        }
      }
      
      setAlertObject({
        status: res.data?.errors?.length > 0 ? "warning" : "success",
        message: resultMessage,
      });
      
      // Give backend extra time to complete async operations (workaround for backend async map issue)
      setTimeout(() => {
        close(); // Close dialog first
        window.location.reload(); // Then reload
      }, 2500); // Increased from 1500 to 2500ms
    } catch (e) {
      console.log("Upload error:", e);
      const errorMessage = e.response?.data?.message || e.response?.data?.error || e.message || "Upload failed";
      setAlertObject({
        status: "error",
        message: `Upload failed: ${errorMessage}`,
      });
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      maxWidth={"md"}
      fullWidth
      open={open}
      onClose={close}
      aria-labelledby="responsive-dialog-title"
    >
      <GridContainer spacing={2}>
        <GridItem xs={12} sm={12}>
          <Card>
            <CardHeader color="primary">
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <h4 className={classes.cardTitleWhite}>Upload File</h4>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={_onUpload}
                  disabled={isUploading || validationErrors.length > 0}
                >
                  {isUploading ? "Uploading..." : "Upload"}
                </Button>
              </Box>
            </CardHeader>
            <CardBody>
              <GridContainer>
                <GridItem xs={12} sm={12} md={6}>
                  <input
                    className="btn"
                    type="file"
                    onChange={_changeHandler}
                    style={{ padding: "10px" }}
                    accept=".xlsx,.xls,.csv"
                  />

                  {validationErrors.length > 0 && (
                    <Box mt={2} p={2} bgcolor="error.light" borderRadius={1}>
                      <h5 style={{ color: 'white', margin: '0 0 10px 0' }}>Validation Errors:</h5>
                      {validationErrors.map((error, index) => (
                        <div key={index} style={{ color: 'white', fontSize: '12px' }}>
                          • {error}
                        </div>
                      ))}
                    </Box>
                  )}

                  <OutTable
                    data={rows}
                    columns={cols}
                    tableClassName="ExcelTable2007"
                    tableHeaderRowClass="heading"
                  />
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
      {alertObject ? (
        <Snackbar
          open={alertObject}
          autoHideDuration={2000}
          onClose={handleClose}
        >
          <Alert onClose={handleClose} severity={alertObject.status}>
            {alertObject.message}
          </Alert>
        </Snackbar>
      ) : null}
    </Dialog>
  );
}
